import React, { useState, useEffect } from "react";
import "./App.css";
import Header from "./components/Header";
import Dashboard from "./components/Dashboard";
import OrganizationList from "./components/OrganizationList";
import OrganizationForm from "./components/OrganizationForm";
import EmployeeList from "./components/EmployeeList";
import EmployeeForm from "./components/EmployeeForm";
import RoleList from "./components/RoleList";
import RoleForm from "./components/RoleForm";
import {
  getOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  getRoles,
  createRole,
  updateRole,
  deleteRole,
  updateRoleEndpoints,
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  ApiError,
} from "./api";

function App({ onLogout }) {
  const [organizations, setOrganizations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activeView, setActiveView] = useState("dashboard");
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  const loadData = async () => {
    setApiError(null);
    try {
      const [orgs, rolesList, usersData] = await Promise.all([
        getOrganizations(),
        getRoles(),
        listUsers({ limit: 1000 }),
      ]);
      setOrganizations(orgs);
      setRoles(rolesList);
      setEmployees(usersData.users || []);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setApiError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const addOrganization = async (organization) => {
    setApiError(null);
    try {
      const created = await createOrganization(organization);
      setOrganizations((prev) => [...prev, created]);
      setActiveView("organizations");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setApiError(err.message || "Failed to create organization");
    }
  };

  const updateOrganizationHandler = async (updatedOrganization) => {
    setApiError(null);
    try {
      const updated = await updateOrganization(updatedOrganization.id, updatedOrganization);
      setOrganizations((prev) =>
        prev.map((org) => (org.id === updated.id ? updated : org))
      );
      setSelectedOrganization(null);
      setActiveView("organizations");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setApiError(err.message || "Failed to update organization");
    }
  };

  const deleteOrganizationHandler = async (id) => {
    setApiError(null);
    try {
      await deleteOrganization(id);
      setOrganizations((prev) => prev.filter((org) => org.id !== id));
      setEmployees((prev) => prev.filter((emp) => emp.organizationId !== id));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setApiError(err.message || "Failed to delete organization");
    }
  };

  const addEmployee = async (employee) => {
    setApiError(null);
    try {
      const created = await createUser(employee);
      setEmployees((prev) => [...prev, created]);
      setActiveView("employees");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setApiError(err.message || "Failed to create employee");
    }
  };

  const updateEmployeeHandler = async (updatedEmployee) => {
    setApiError(null);
    try {
      const updated = await updateUser(updatedEmployee.id, updatedEmployee);
      setEmployees((prev) =>
        prev.map((emp) => (emp.id === updated.id ? updated : emp))
      );
      setSelectedEmployee(null);
      setActiveView("employees");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setApiError(err.message || "Failed to update employee");
    }
  };

  const deleteEmployeeHandler = async (id) => {
    setApiError(null);
    try {
      await deleteUser(id);
      setEmployees((prev) => prev.filter((emp) => emp.id !== id));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setApiError(err.message || "Failed to delete employee");
    }
  };

  const addRoleHandler = async (roleData, endpointIds = []) => {
    setApiError(null);
    try {
      const created = await createRole(roleData);
      if (endpointIds.length > 0) {
        await updateRoleEndpoints(created.id, endpointIds);
      }
      setRoles((prev) => [...prev, created]);
      setSelectedRole(null);
      setActiveView("roles");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setApiError(err.message || "Failed to create role");
    }
  };

  const updateRoleHandler = async (updatedRole, endpointIds) => {
    setApiError(null);
    try {
      const updated = await updateRole(updatedRole.id, updatedRole);
      if (endpointIds != null) {
        await updateRoleEndpoints(updatedRole.id, endpointIds);
      }
      setRoles((prev) =>
        prev.map((role) => (role.id === updated.id ? updated : role))
      );
      setSelectedRole(null);
      setActiveView("roles");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setApiError(err.message || "Failed to update role");
    }
  };

  const deleteRoleHandler = async (id) => {
    setApiError(null);
    try {
      await deleteRole(id);
      setRoles((prev) => prev.filter((role) => role.id !== id));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setApiError(err.message || "Failed to delete role");
    }
  };

  if (loading) {
    return (
      <div className="app">
        <div className="main-content" style={{ textAlign: "center", padding: "4rem" }}>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header activeView={activeView} setActiveView={setActiveView} onLogout={onLogout} />

      <main className="main-content">
        {apiError && (
          <div
            className="api-error"
            style={{
              background: "#fde8e8",
              color: "#c0392b",
              padding: "1rem",
              borderRadius: "6px",
              marginBottom: "1rem",
            }}
            role="alert"
          >
            {apiError}
          </div>
        )}

        {activeView === "dashboard" && (
          <Dashboard
            organizations={organizations}
            employees={employees}
            roles={roles}
            setActiveView={setActiveView}
          />
        )}

        {activeView === "organizations" && (
          <OrganizationList
            organizations={organizations}
            setSelectedOrganization={setSelectedOrganization}
            setActiveView={setActiveView}
            deleteOrganization={deleteOrganizationHandler}
          />
        )}

        {activeView === "add-organization" && (
          <OrganizationForm
            addOrganization={addOrganization}
            updateOrganization={updateOrganizationHandler}
            selectedOrganization={selectedOrganization}
            setActiveView={setActiveView}
          />
        )}

        {activeView === "employees" && (
          <EmployeeList
            employees={employees}
            organizations={organizations}
            roles={roles}
            setSelectedEmployee={setSelectedEmployee}
            setActiveView={setActiveView}
            deleteEmployee={deleteEmployeeHandler}
          />
        )}

        {activeView === "add-employee" && (
          <EmployeeForm
            addEmployee={addEmployee}
            updateEmployee={updateEmployeeHandler}
            selectedEmployee={selectedEmployee}
            organizations={organizations}
            roles={roles}
            setActiveView={setActiveView}
          />
        )}

        {activeView === "roles" && (
          <RoleList
            roles={roles}
            employees={employees}
            setSelectedRole={setSelectedRole}
            setActiveView={setActiveView}
            deleteRole={deleteRoleHandler}
          />
        )}

        {activeView === "add-role" && (
          <RoleForm
            addRole={addRoleHandler}
            updateRole={updateRoleHandler}
            selectedRole={selectedRole}
            setActiveView={setActiveView}
            setApiError={setApiError}
          />
        )}
      </main>

      <footer className="footer">
        <p>CRM System © 2026 – Organization and Employee Management</p>
      </footer>
    </div>
  );
}

export default App;
